"use client";

import dynamic from "next/dynamic";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@/redux/hooks";
import type { RootState } from "@/redux/store";

const ProductFruits = dynamic(
  () => import("react-product-fruits").then((mod) => mod.ProductFruits),
  { ssr: false }
);

type ProductFruitsWindow = Window & {
  productFruits?: {
    services?: {
      destroy?: () => void;
    };
  };
};

export function ProductFruitsProvider() {
  const [mounted, setMounted] = useState(false);
  const user = useAppSelector((state: RootState) => state.user.user);

  const workspaceCode =
    process.env.NEXT_PUBLIC_PRODUCT_FRUITS_WORKSPACE_CODE?.trim() ?? "";
  const language = (
    process.env.NEXT_PUBLIC_PRODUCT_FRUITS_LANGUAGE?.trim() || "en"
  ).toLowerCase();
  const isEnabled = process.env.NEXT_PUBLIC_PRODUCT_FRUITS_ENABLED === "true";

  const userInfo = useMemo(() => {
    if (!user) return null;
    const username = (user.id ?? user.email ?? "").trim();
    if (!username) return null;

    const fullname = (user.fullname ?? "").trim();
    const [first = "", ...rest] = fullname.split(/\s+/);
    return {
      username,
      email: user.email ?? "",
      firstname: first,
      lastname: rest.join(" "),
      role: user.role?.title ?? "",
      props: {
        organizationId: user.organization?.id ?? "",
        organizationName: user.organization?.name ?? "",
      },
    };
  }, [user]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (userInfo) return;
    (window as ProductFruitsWindow).productFruits?.services?.destroy?.();
  }, [userInfo]);

  if (!mounted || !isEnabled || !workspaceCode || !userInfo) return null;

  return (
    <ProductFruits
      workspaceCode={workspaceCode}
      language={language}
      user={userInfo}
      lifeCycle="neverUnmount"
    />
  );
}
